import { TrendingDown, TrendingUp, Info } from "lucide-react";
import { CardProps } from "./types";
import { Loader } from "../Loader";
import { cn } from "@/utils/cn";

export function Card({
  title,
  value,
  subtitle,
  variation,
  icon,
  infoHint,
  variant = "default",
  className,
  children,
  loading = false,
  onClick,
}: CardProps) {
  const variantStyles: Record<string, string> = {
    default:  "bg-white border border-slate-200 shadow-card",
    outlined: "bg-white border-2 border-brand-200",
    elevated: "bg-white shadow-lg",
  };

  const hasVariation = variation !== undefined && variation !== null;
  const isPositive   = hasVariation && variation! >= 0;

  return (
    <div
      className={cn(
        "rounded-xl p-5 transition-all",
        variantStyles[variant],
        onClick && "cursor-pointer hover:shadow-md hover:-translate-y-0.5",
        className
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {loading ? (
        <div className="flex items-center justify-center h-16">
          <Loader size="sm" />
        </div>
      ) : children ? (
        children
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {title && (
                <div className="flex items-start gap-1">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight line-clamp-2">
                    {title}
                  </p>
                  {infoHint && (
                    <span
                      className="group relative mt-0.5 flex-shrink-0 text-slate-400 hover:text-brand-600"
                      tabIndex={0}
                      role="note"
                      aria-label={infoHint}
                    >
                      <Info className="h-3.5 w-3.5" />
                      <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 hidden w-56 -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] normal-case font-normal normal-tracking text-slate-600 shadow-modal group-hover:block group-focus:block">
                        {infoHint}
                      </span>
                    </span>
                  )}
                </div>
              )}
              {value !== undefined && (
                <p
                  className="mt-1 text-xs sm:text-sm md:text-base font-bold text-slate-800 tabular-nums leading-none whitespace-nowrap min-w-0"
                  title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
                >
                  {value}
                </p>
              )}
              {subtitle && (
                <p className="mt-0.5 text-[11px] text-slate-400 line-clamp-3 break-words">{subtitle}</p>
              )}
            </div>

            {icon && (
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600">
                {icon}
              </div>
            )}
          </div>

          {hasVariation && (
            <div
              className={cn(
                "mt-3 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full",
                isPositive
                  ? "text-emerald-700 bg-emerald-50"
                  : "text-red-700 bg-red-50"
              )}
            >
              {isPositive ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              {isPositive ? "+" : ""}
              {variation!.toFixed(1)}%
            </div>
          )}
        </>
      )}
    </div>
  );
}
